import { useEffect, useState } from "react";
import { FileText, Save, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

export default function AdminPages() {
  const [pages, setPages] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ title: "", content: "", active: true });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/pages").then((r) => setPages(r.data)).catch(() => setPages([]));
  useEffect(() => {
    load();
  }, []);

  const pick = (p) => {
    setSelected(p.slug);
    setForm({ title: p.title, content: p.content, active: p.active });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/pages/${selected}`, { slug: selected, ...form });
      toast.success("Página salva!");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível salvar."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 sm:p-8 max-w-5xl" data-testid="admin-pages">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight mb-2">Páginas Institucionais</h1>
      <p className="text-sm text-gray-500 mb-6">
        Textos de confiança da loja (sobre, entrega, garantia). Aparecem no rodapé do site e ajudam o Google a conhecer a HG Móveis.
      </p>
      {pages === null ? (
        <div className="animate-pulse h-40 bg-white rounded-2xl" />
      ) : (
        <div className="grid md:grid-cols-[240px_1fr] gap-6 items-start">
          <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
            {pages.map((p) => (
              <button
                key={p.slug}
                onClick={() => pick(p)}
                className={`w-full flex items-center gap-2.5 px-4 py-3 text-sm font-medium text-left transition-colors border-b border-gray-50 last:border-0 ${
                  selected === p.slug ? "bg-brand text-white" : "text-gray-700 hover:bg-blue-50"
                }`}
                data-testid={`page-item-${p.slug}`}
              >
                <FileText className="w-4 h-4 shrink-0" />
                <span className="flex-1 truncate">{p.title}</span>
                {!p.active && <EyeOff className="w-3.5 h-3.5 opacity-60" />}
              </button>
            ))}
          </div>
          {selected ? (
            <div className="bg-white border border-gray-100 rounded-xl p-5 space-y-4" data-testid="page-editor">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-slate-400">Endereço: /pagina/{selected}</p>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="page-active" />
                  {form.active ? <span className="flex items-center gap-1"><Eye className="w-4 h-4" /> Visível</span> : "Oculta"}
                </label>
              </div>
              <div>
                <Label>Título da página</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" data-testid="page-title" />
              </div>
              <div>
                <Label>Conteúdo (uma frase/parágrafo por linha)</Label>
                <Textarea
                  rows={12}
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  className="mt-1.5 text-sm leading-relaxed"
                  data-testid="page-content"
                />
              </div>
              <Button onClick={save} disabled={saving} className="rounded-full" data-testid="page-save">
                <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar página"}
              </Button>
            </div>
          ) : (
            <div className="bg-white border border-dashed border-gray-200 rounded-xl p-10 text-center text-sm text-gray-400">
              Selecione uma página ao lado para editar.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
